import { gsap } from './gsap-config.js';

class FixedScrollingSection {

    constructor(options = {}) {

        this.options = options;

        const consoleMessage = `Please make sure you're creating FixedScrollingSection like this:
        new FixedScrollingSection({ trigger: '#example' })`;

        // Break out if no options were passed
        if(Object.keys(this.options).length === 0) {

            throw Error(consoleMessage);

        }

        // Break out if wrong type of options are passed
        if(!this.options.trigger) {

            throw Error(consoleMessage);

        }

        this.trigger = this.options.trigger;
        this.imageContainer = this.options.trigger.querySelector('[data-scrolling-section-image-container]');
        this.images = this.imageContainer.querySelectorAll('[data-scrolling-section-image]');
        this.text = gsap.utils.toArray('[data-scrolling-section-text]', this.trigger);
        this.numberOfSections = (this.text.length === this.images.length) ? this.text.length : 0;
        this.fadeDuration = 20;

        if(!this.imageContainer) {

            throw Error(consoleMessage);

        }

        if (this.numberOfSections > 1) {

            this.trigger.classList.add('fixed-scrolling-section--init');

            let tl = gsap.timeline({
                scrollTrigger: {
                    trigger: this.trigger,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: true,
                    toggleClass: 'is-active',
                    onUpdate: ({progress, direction, isActive}) => {
                        // going forward
                        if(direction === 1) {
                            this.trigger.classList.remove('reverse');
                            this.trigger.classList.add('forward');
                        } else {
                            this.trigger.classList.remove('forward');
                            this.trigger.classList.add('reverse');
                        }
                    },
                    onEnterBack: () => {
                        this.trigger.classList.remove('has-hit-bottom');
                    },
                    onLeave: () => {
                        this.trigger.classList.add('has-hit-bottom');
                    }
                }
            });

            this.tl = tl;

            // Once page is fully loaded - Create scroll animations and add to gsap timeline.
            // Ran into an issue where fonts not being loaded yet were changing the dimensions
            // of the scrolling sections dimensions.
            if(document.readyState === 'complete') {

                this.createSections();

            } else {

                window.addEventListener('load', () => {
                    this.createSections();
                });

            }

        }

    }

    scrollerHeight() {
        return this.trigger.getBoundingClientRect().height;
    }

    windowPercentage() {
        return (window.innerHeight / 2) / this.scrollerHeight() * 100;
    }

    createSections() {
        let transition_total = 0;
        const sectionPercentage = (text) => (text.getBoundingClientRect().height / this.scrollerHeight()) * 100;

        this.text.forEach((text, i) => {

            // Matching image
            let image = this.images[i];
            let static_duration = sectionPercentage(text);

            image.style.zIndex = -i;

            // If first section
            if(i === 0) {

                static_duration = static_duration - this.windowPercentage();

                this.tl.to(image, { duration: static_duration }, 0)
                .to(image, {
                    autoAlpha: 0,
                    duration: this.fadeDuration,
                    autoRound: false,
                    ease: 'sine.out',
                }, `>-${ this.fadeDuration / 2 }`);

            // Sections in between
            } else if (i > 0 && i < (this.numberOfSections - 1)) {

                // Transition in
                this.tl.fromTo(image, {
                    autoAlpha: 0,
                }, {
                    autoAlpha: 1,
                    duration: this.fadeDuration,
                    ease: 'sine.out',
                    onStart: () => image.classList.add('transitioning-in'),
                    onComplete: () => image.classList.remove('transitioning-in'),
                }, '<')
                // Empty transition to add delay in timeline
                .to(image, { duration: static_duration }, `>-${ this.fadeDuration / 2 }`)
                // Transition out
                .to(image, {
                    autoAlpha: 0,
                    duration: this.fadeDuration,
                    autoRound: false,
                    ease: 'sine.out',
                    onStart: () => image.classList.add('transitioning-out'),
                    onComplete: () => image.classList.remove('transitioning-out'),
                }, `>-${ this.fadeDuration / 2 }`);

            // If last section
            } else {
                // Transition in
                this.tl.fromTo(image, {
                    autoAlpha: 0,
                }, {
                    autoAlpha: 1,
                    duration: this.fadeDuration,
                    ease: 'sine.out',
                    onStart: () => image.classList.add('transitioning-in'),
                    onComplete: () => image.classList.remove('transitioning-in'),
                }, '<')
                .to(image, { duration: this.windowPercentage() }, `>-${ this.fadeDuration / 2 }`);

            }

            // Keep track of total time
            transition_total = transition_total + static_duration;
        });

        this.trigger.classList.remove('fixed-scrolling-section--init');
        this.trigger.classList.add('fixed-scrolling-section--created');
    }

}

const scrollingSections = document.querySelectorAll('[data-scrolling-section]');

scrollingSections.forEach((section) => {
    new FixedScrollingSection({ trigger: section });
});
